import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { COLLECT_ROLES, type HouseDetail } from '@mahallu/shared';
import { loadErrorKey, useHouseAccount, useMe } from '../api';
import { Icon } from '../components/Icon';
import { Badge } from '../components/Parts';
import { inr, monthLabel } from '../format';
import { CollectForm } from './CollectForm';

/** House drawer › Varisankhya: dues month by month, what is outstanding, and the house's receipts. */
export function HouseAccountTab({ house }: { house: HouseDetail }) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const canCollect = Boolean(me.data?.role && COLLECT_ROLES.includes(me.data.role));
  const account = useHouseAccount(house.id);
  const [collecting, setCollecting] = useState(false);
  const a = account.data;
  if (account.isError) return <p className="note">{t(loadErrorKey(account.error))}</p>;
  if (!a) return <p className="note">{t('drawer.loading')}</p>;
  return (
    <>
      {canCollect ? (
        <div className="actions">
          <button type="button" className="btn sm primary" onClick={() => setCollecting(true)}>
            <Icon name="wallet" />
            {t('collect.title')}
          </button>
        </div>
      ) : null}
      <dl className="kv">
        <div>
          <dt>{t('account.monthly')}</dt>
          <dd className="num">{inr(a.monthlyRateInr)}</dd>
        </div>
        <div>
          <dt>{t('account.arrears')}</dt>
          <dd className="num">
            {inr(a.arrearsInr)} {a.arrearsMonths ? <span className="note">{t('unit.months', { n: a.arrearsMonths })}</span> : null}
          </dd>
        </div>
        <div>
          <dt>{t('account.outstanding')}</dt>
          <dd className="num">{inr(a.outstandingInr)}</dd>
        </div>
      </dl>
      <div className="callout small" style={{ margin: '12px 0' }}>
        {t('drawer.arrearsNote')}
      </div>
      <h4 className="sub-h">{t('account.dues')}</h4>
      {a.dues.length ? (
        <div className="due-grid">
          {a.dues.map((d) => (
            <span key={d.id} className={`due-chip ${d.paidInr >= d.amountInr ? 'paid' : d.paidInr ? 'part' : d.period < a.currentPeriod ? 'late' : 'open'}`} title={`${inr(d.paidInr)} / ${inr(d.amountInr)}`}>
              {monthLabel(d.period, i18n.language)}
            </span>
          ))}
        </div>
      ) : (
        <p className="note">{t('account.noDues')}</p>
      )}
      <h4 className="sub-h">{t('account.receipts')}</h4>
      {a.receipts.length ? (
        <ul className="history">
          {a.receipts.map((r) => (
            <li key={r.id}>
              <Link to={`/receipts/${r.id}/print`} target="_blank" rel="noopener" className="num">
                {r.receiptNo}
              </Link>{' '}
              · {r.receivedOn} · {i18n.language === 'ml' && r.fund.nameMl ? r.fund.nameMl : r.fund.nameEn} · <b className="num">{inr(r.amountInr)}</b>
              {r.status === 'CANCELLED' ? (
                <>
                  {' '}
                  <Badge tone="neutral">{t('rcpt.CANCELLED')}</Badge>
                </>
              ) : null}
              {r.periods.length ? <div className="note">{r.periods.map((p) => monthLabel(p, i18n.language)).join(', ')}</div> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="note">{t('account.noReceipts')}</p>
      )}
      {collecting ? <CollectForm house={{ id: house.id, houseNo: house.houseNo, nameEn: house.nameEn }} onClose={() => setCollecting(false)} /> : null}
    </>
  );
}
